/* MasterForce Buylist V2.5 — Cotización referencial, sin pagos ni crédito automático.
   La información de WhatsApp se puede editar desde el navegador. Siempre verificar en tienda. */
(() => {
  'use strict';
  const GLOBAL = '__mfBuylistV2';
  if (window[GLOBAL]) { window[GLOBAL].init(); return; }
  const instances = new WeakMap();
  const routeRoot = () => ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/?$/, '/');
  const norm = value => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  const csvSet = value => new Set(String(value || '').split(',').map(norm).filter(Boolean));
  const node = (tag, className, content) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (content !== undefined && content !== null) el.textContent = String(content);
    return el;
  };
  const list = array => Array.isArray(array) ? array.map(norm) : typeof array === 'string' ? array.split(',').map(norm) : [];
  // MasterForce uses separate products for Normal / Reverse and includes the rarity
  // in the product title. Explicit product titles help detect generic SEO tags.
  const titleSignals = product => {
    const title = norm(product.title);
    const explicitReverse = /(?:^|\s)(?:reverse(?:\s+(?:holo|foil))?|reversa)(?=\s|$)/.test(title);
    const explicitCommon = /(?:^|\s)(?:uncommon|common|comun|poco\s+comun|infrecuente)(?=\s|$)/.test(title);
    const explicitPremium = /(?:^|\s)(?:special illustration rare|illustration rare|mega hyper rare|hyper rare|double rare|ultra rare|secret rare|shiny rare|super rare|holo rare|rare holo|rainbow rare|starlight rare|ghost rare|collector rare|collectors rare|ultimate rare|prismatic secret rare|quarter century secret rare)(?=\s|$)/.test(title);
    return {explicitReverse, explicitCommon, explicitPremium};
  };
  const finiteCents = n => n !== null && n !== undefined && n !== '' && Number.isSafeInteger(Number(n)) && Number(n) >= 0 ? Number(n) : null;
  // Predictive Search JSON returns prices in currency units (e.g. "89.90").
  // Ajax Product API and Liquid product.price return integer cents (e.g. 8990).
  const predictivePriceCents = value => {
    if (value === null || value === undefined || value === '') return null;
    const str = String(value).trim();
    if (!/^\d+(?:\.\d{1,2})?$/.test(str)) return null;
    const [whole, fractions = ''] = str.split('.');
    const result = Number(whole) * 100 + Number(fractions.padEnd(2, '0'));
    return Number.isSafeInteger(result) ? result : null;
  };
  const clampQty = q => Math.min(99999, Math.max(1, Math.trunc(Number(q) || 1)));
  const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

  class Buylist {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-mf-config]').textContent);
      this.key = this.config ? root.dataset.mfStorageKey : '';
      this.allowed = csvSet(this.config.allowedRarities);
      // Existing stores may retain the V2.1 setting, which omitted this Pokémon rarity.
      this.allowed.add('mega hyper rare');
      this.common = csvSet(this.config.forbiddenCommon);
      this.reverse = csvSet(this.config.forbiddenReverse);
      this.singleTags = csvSet(this.config.singleTags);
      this.query = root.querySelector('[data-mf-query]');
      this.form = root.querySelector('[data-mf-search-form]');
      this.loading = root.querySelector('[data-mf-loading]');
      this.results = root.querySelector('[data-mf-results]');
      this.status = root.querySelector('[data-mf-search-status]');
      this.more = root.querySelector('[data-mf-more]');
      this.cartBox = root.querySelector('[data-mf-cart-items]');
      this.sendBtn = root.querySelector('[data-mf-send]');
      this.clearBtn = root.querySelector('[data-mf-clear]');
      this.dialog = root.querySelector('[data-mf-dialog]');
      this.items = [];
      this.productCache = new Map();
      this.currentSearch = '';
      this.pageNext = null;
      this.mode = 'suggest';
      this.seq = 0;
      this.abort = null;
      this.timer = null;
      this.copyText = '';
      this.events = new AbortController();
      this.format = new Intl.NumberFormat('es-MX', {style:'currency', currency: 'MXN'});
      this.attach();
      this.restore();
      this.renderCart();
    }
    get currencyOK() {
      const active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      return norm(this.config.storeCurrency) === 'mxn' && (!active || norm(active) === 'mxn');
    }
    money(cents) { return this.format.format((Number(cents) || 0) / 100); }
    setStatus(message) { this.status.textContent = message; }
    setLoading(busy) { this.loading.hidden = !busy; }
    keyFor(item) { return `${item.variantId}|${item.condition}|${item.special}`; }
    hasTag(tags, set) { return list(tags).some(tag => set.has(tag)); }
    isSingle(product) {
      const tags = list(product.tags);
      if (tags.some(t => this.singleTags.has(t))) return true;
      const games = ['pokemon', 'pokemon tcg', 'yu gi oh', 'yugioh', 'magic', 'magic the gathering', 'mtg', 'one piece', 'one piece card game', 'digimon', 'lorcana', 'disney lorcana'];
      return tags.some(t => games.includes(t)) && tags.some(t => this.allowed.has(t) || this.common.has(t) || this.reverse.has(t));
    }
    classify(product, condition, special, cents) {
      if (special === 'fake') return { status:'rejected', reason:'No compramos cartas falsificadas ni reproducciones.' };
      if (condition === 'DMG') return { status:'rejected', reason:'No compramos cartas dañadas (DMG).' };
      const tags = list(product.tags);
      const {explicitReverse, explicitCommon, explicitPremium} = titleSignals(product);
      const matchCommon = tags.find(tag => this.common.has(tag));
      const matchReverse = tags.find(tag => this.reverse.has(tag));
      const matchAllowed = tags.find(tag => this.allowed.has(tag));
      const tagLabel = tag => tag ? ` (tag detectado: «${tag}»).` : '.';

      // The product title explicitly identifies the edition for MasterForce's
      // separate Normal/Reverse products. Such exclusions cannot be overridden.
      if (explicitCommon) return {status:'rejected', reason:'No compramos cartas Common o Uncommon (identificado en el nombre).'};
      if (explicitReverse) return {status:'rejected', reason:'No compramos cartas Reverse Holo (identificado en el nombre).'};

      // A high-rarity title AND an allowed rarity tag disagree with a generic
      // 'reverse' SEO tag. Trust the exact non-Reverse edition named in the title;
      // retain an explicit warning on every resulting offer.
      const reverseTagConflict = Boolean(matchReverse && explicitPremium && matchAllowed);
      if (matchCommon && explicitPremium) {
        return {status:'review', reason:`Tags contradictorios: rareza especial en el nombre, pero también «${matchCommon}». Revisión manual.`};
      }
      if (matchCommon) return {status:'rejected', reason:'No compramos cartas Common o Uncommon' + tagLabel(matchCommon)};
      if (matchReverse && !reverseTagConflict) return {status:'rejected', reason:'No compramos cartas Reverse Holo' + tagLabel(matchReverse)};
      if (!this.isSingle(product)) return {status:'review', reason:'No se confirmó que este producto sea una carta individual.'};
      if (special === 'altered') return {status:'review', reason:'Carta firmada, alterada o reparada: valoración manual.'};
      if (!matchAllowed) return {status:'review', reason:'Rareza no reconocida en los tags; revisión manual.'};
      if (!this.currencyOK) return {status:'review', reason:'La moneda de visualización no es MXN; revisión manual.'};
      if (cents === null || cents <= 0) return {status:'review', reason:'Precio de venta no válido; revisión manual.'};
      const multiplier = Number(this.config.conditions[condition]);
      if (!Number.isFinite(multiplier)) return {status:'review', reason:'Condición de carta no reconocida.'};
      const cash = Math.round(cents * Number(this.config.buyRate) * multiplier);
      const credit = Math.round(cash * Number(this.config.creditMultiplier));
      if (cash < this.config.minCashCents) return {status:'rejected', reason:`Oferta individual ${this.money(cash)} inferior al mínimo de ${this.money(this.config.minCashCents)}.`};
      const reason = reverseTagConflict
        ? `Estimación provisional. AVISO: el tag «${matchReverse}» contradice la rareza del nombre («${matchAllowed}»). Verifica y corrige ese tag; confirmar edición y acabado al recibir la carta.`
        : 'Cotización estimada; sujeta a inspección.';
      return { status:'eligible', reason, cash, credit };
    }
    assess(product, variant, condition, special) {
      const price = finiteCents(variant && variant.price);
      const value = this.classify(product, condition, special, price);
      return {...value, cashCents: value.status === 'eligible' ? value.cash : 0, creditCents: value.status === 'eligible' ? value.credit : 0, priceCents: price};
    }
    attach() {
      const signal = this.events.signal;
      this.query.addEventListener('input', () => {
        clearTimeout(this.timer);
        const q = this.query.value.trim();
        if (q.length < 3) {
          this.cancelSearch(); this.results.replaceChildren(); this.more.hidden = true;
          this.setStatus('Escribe al menos tres caracteres para buscar.'); return;
        }
        this.timer = setTimeout(() => this.suggest(q), 350);
      }, {signal});
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        const q = this.query.value.trim();
        if (q.length < 3) { this.setStatus('Escribe al menos tres caracteres.'); return; }
        this.fullSearch(q, 1, false);
      }, {signal});
      this.more.addEventListener('click', () => {
        if (this.mode === 'suggest') this.fullSearch(this.currentSearch, 1, false);
        else if (this.pageNext) this.fullSearch(this.currentSearch, this.pageNext, true);
      }, {signal});
      this.results.addEventListener('click', event => {
        const trigger = event.target.closest('[data-mf-prepare],[data-mf-add]');
        if (!trigger) return;
        const card = trigger.closest('[data-mf-result-card]');
        if (!card) return;
        if (trigger.hasAttribute('data-mf-prepare')) this.prepare(card);
        else this.addFromCard(card);
      }, {signal});
      this.results.addEventListener('change', event => {
        const card = event.target.closest('[data-mf-result-card]');
        if (card && card.dataset.details === 'yes') this.updatePreview(card);
      }, {signal});
      this.cartBox.addEventListener('change', async event => {
        const select = event.target.closest('[data-mf-change-condition]');
        if (!select) return;
        const item = this.items.find(i => this.keyFor(i) === select.dataset.key);
        if (!item || !['NM','LP','MP','HP','DMG'].includes(select.value)) return;
        item.condition = select.value;
        item.status = 'review'; item.reason = 'Actualizando cotización…'; item.cashCents = 0; item.creditCents = 0;
        const duplicate = this.items.find(other => other !== item && this.keyFor(other) === this.keyFor(item));
        if (duplicate) { duplicate.qty = clampQty(duplicate.qty + item.qty); duplicate.status = 'review'; duplicate.reason = 'Actualizando cotización…'; duplicate.cashCents = 0; duplicate.creditCents = 0; this.items = this.items.filter(x => x !== item); }
        this.renderCart();
        try {
          const product = await this.getProduct(item.handle, true);
          const variant = product.variants.find(v => String(v.id) === item.variantId);
          if (variant) {
            const updated = this.assess(product, variant, item.condition, item.special);
            Object.assign(item, updated);
            if (duplicate) Object.assign(duplicate, updated);
          } else {
            item.status = 'review'; item.reason = 'La variante ya no está disponible.';
          }
        } catch (_) {
          item.status = 'review'; item.reason = 'No se pudo verificar el cambio de condición.';
        }
        this.save(); this.renderCart();
      }, {signal});
      this.cartBox.addEventListener('click', event => {
        const button = event.target.closest('[data-action]');
        if (!button) return;
        const key = button.dataset.key;
        const item = this.items.find(i => this.keyFor(i) === key);
        if (!item) return;
        if (button.dataset.action === 'remove') this.items = this.items.filter(i => i !== item);
        if (button.dataset.action === 'plus') item.qty = clampQty(item.qty + 1);
        if (button.dataset.action === 'minus') {
          if (item.qty === 1) this.items = this.items.filter(i => i !== item);
          else item.qty -= 1;
        }
        this.save(); this.renderCart();
      }, {signal});
      this.clearBtn.addEventListener('click', () => {
        if (this.items.length && window.confirm('¿Vaciar toda tu lista de venta?')) {
          this.items = []; this.save(); this.renderCart();
        }
      }, {signal});
      this.sendBtn.addEventListener('click', () => this.reviewForWhatsApp(), {signal});
      this.dialog.addEventListener('click', event => {
        if (event.target.closest('[data-mf-close-dialog]')) this.dialog.close();
        if (event.target.closest('[data-mf-copy]')) this.copyToClipboard();
      }, {signal});
    }
    cancelSearch() {
      this.seq++; clearTimeout(this.timer);
      if (this.abort) this.abort.abort(); this.abort = null;
      this.setLoading(false);
    }
    productFromPredictive(p) {
      const min = predictivePriceCents(p.price_min ?? p.price);
      const max = predictivePriceCents(p.price_max ?? p.price);
      return { id: String(p.id), title: String(p.title || ''), handle: String(p.handle || ''), tags: Array.isArray(p.tags) ? p.tags : [],
        url: '/products/' + encodeURIComponent(p.handle || ''),
        image: p.featured_image && p.featured_image.url || p.image || '',
        priceCents: min, priceVaries: min !== null && max !== null && min !== max };
    }
    async suggest(q) {
      this.cancelSearch();
      const seq = this.seq, controller = new AbortController(); this.abort = controller;
      this.currentSearch = q; this.mode = 'suggest'; this.pageNext = null;
      this.setLoading(true); this.more.hidden = true;
      try {
        const params = new URLSearchParams({q, 'resources[type]':'product', 'resources[limit]':'10', 'resources[options][unavailable_products]':'show'});
        const response = await fetch(`${routeRoot()}search/suggest.json?${params}`, {signal:controller.signal, credentials:'same-origin'});
        if (!response.ok) throw Error(`Búsqueda: HTTP ${response.status}`);
        const json = await response.json();
        if (seq !== this.seq) return;
        const products = (json.resources?.results?.products || []).map(p => this.productFromPredictive(p)).filter(p => p.handle && this.isSingle(p));
        this.renderResults(products, false);
        this.setStatus(`${products.length} carta(s) identificada(s) entre las sugerencias. Usa «Buscar todas» para más resultados.`);
        this.more.textContent = 'Buscar todos los resultados'; this.more.hidden = false;
      } catch (error) {
        if (error.name !== 'AbortError' && seq === this.seq) this.setStatus('No se pudo completar la búsqueda. Intenta nuevamente.');
      } finally { if (seq === this.seq) this.setLoading(false); }
    }
    async fullSearch(q, page, append) {
      if (!append) this.cancelSearch();
      else if (this.abort) this.abort.abort();
      const seq = this.seq, controller = new AbortController(); this.abort = controller;
      this.currentSearch = q; this.mode = 'full';
      this.setLoading(true); this.more.disabled = true;
      try {
        const params = new URLSearchParams({q, type:'product', page:String(page), section_id:'mf-buylist-search-results'});
        const response = await fetch(`${routeRoot()}search?${params}`, {signal:controller.signal, credentials:'same-origin'});
        if (!response.ok) throw Error(`Resultados: HTTP ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const wrapper = doc.querySelector('[data-mf-search-page]');
        if (!wrapper) throw Error('Sección auxiliar de búsqueda ausente; revisa su instalación.');
        if (seq !== this.seq) return;
        const products = Array.from(wrapper.querySelectorAll('[data-mf-item]')).map(n => {
          let tags = [];
          try { tags = JSON.parse(n.dataset.tags || '[]'); } catch (_) {}
          return { id:n.dataset.id, title:n.dataset.title, handle:n.dataset.handle, url:n.dataset.url,
            image:n.dataset.image, tags,
            priceCents: finiteCents(n.dataset.priceCents), priceVaries: n.dataset.priceVaries === 'true' };
        }).filter(p => p.handle && this.isSingle(p));
        this.renderResults(products, append);
        this.pageNext = Number(wrapper.dataset.mfNext) || null;
        this.more.hidden = !this.pageNext;
        this.more.textContent = 'Ver más resultados';
        this.setStatus(`${this.results.querySelectorAll('[data-mf-result-card]').length} cartas identificadas en las páginas cargadas.${this.pageNext ? ' Hay más resultados.' : ''}`);
      } catch (error) {
        if (error.name !== 'AbortError' && seq === this.seq) {
          this.setStatus('No fue posible cargar la búsqueda ampliada. Comprueba que instalaste la sección auxiliar.');
          if (!append) { this.mode = 'suggest'; this.more.hidden = false; this.more.textContent = 'Reintentar búsqueda ampliada'; }
        }
      } finally { if (seq === this.seq) { this.setLoading(false); this.more.disabled = false; } }
    }
    renderResults(products, append) {
      if (!append) this.results.replaceChildren();
      const existing = new Set(Array.from(this.results.querySelectorAll('[data-mf-result-card]'), el => el.dataset.handle));
      for (const p of products) {
        if (existing.has(p.handle)) continue;
        existing.add(p.handle);
        const card = node('article', 'mf-bv2-result');
        card.dataset.mfResultCard = ''; card.dataset.handle = p.handle;
        card._mfProductPreview = p;
        if (p.image && /^(https?:)?\/\//i.test(p.image)) {
          const img = node('img'); img.src = p.image; img.loading = 'lazy'; img.width = 60; img.height = 84; img.alt = `Carta ${p.title}`;
          card.append(img);
        }
        const body = node('div','mf-bv2-result-info');
        const heading = node('div','mf-bv2-result-title',p.title);
        body.append(heading);
        // Show the Shopify retail price immediately, even for excluded cards.
        // On products with varying prices, this is the lowest price and never a final offer.
        const retail = node('p', 'mf-bv2-retail', p.priceCents === null
          ? 'Precio de venta: pendiente de verificar'
          : `${p.priceVaries ? 'Precio de venta desde' : 'Precio de venta'}: ${this.money(p.priceCents)}`);
        retail.dataset.mfRetail = '';
        body.append(retail);
        const status = node('p','mf-bv2-state');
        status.dataset.mfCardState = '';
        const previewState = this.classify(p, 'NM', 'normal', p.priceVaries ? null : p.priceCents);
        if (previewState.status === 'rejected') {
          // No mostrar mensajes de rechazo ni tags técnicos en resultados públicos.
          // La clasificación y el motivo siguen activos para la lista y WhatsApp.
          status.dataset.state = 'rejected';
          status.hidden = true;
        } else if (p.priceVaries) {
          status.textContent = 'Selecciona la variante exacta para obtener una cotización.';
        } else if (previewState.status === 'eligible') {
          // Interfaz pública limpia: el diagnóstico exacto permanece en la cotización
          // interna enviada a WhatsApp; aquí solo se muestra el importe estimado.
          status.dataset.state = 'eligible';
          status.replaceChildren(node('span', 'mf-bv2-quote-line',
            `Oferta estimada NM: ${this.money(previewState.cash)} efectivo · ${this.money(previewState.credit)} crédito`));
          if (previewState.reason.startsWith('Estimación provisional. AVISO:')) {
            status.append(node('small', 'mf-bv2-quote-note', 'Sujeta a revisión de edición y acabado.'));
          }
        } else {
          status.textContent = previewState.reason;
          status.dataset.state = 'review';
        }
        body.append(status);
        const btn = node('button','mf-bv2-secondary','Preparar cotización');
        btn.type = 'button'; btn.dataset.mfPrepare = '';
        body.append(btn);
        card.append(body); this.results.append(card);
      }
      if (!this.results.children.length) this.results.append(node('p','mf-bv2-empty','No encontramos singles identificables con los tags configurados. Prueba otro término o revisa las etiquetas del catálogo.'));
      else { const empty = this.results.querySelector('.mf-bv2-empty'); if (empty) empty.remove(); }
    }
    async getProduct(handle, force = false) {
      if (!/^[a-zA-Z0-9_-]+$/.test(handle)) throw Error('Handle de producto no válido');
      const cached = this.productCache.get(handle);
      if (!force && cached && Date.now() - cached.at < 180000) return cached.product;
      const url = `${routeRoot()}products/${encodeURIComponent(handle)}.js${force ? '?mf_ref=' + Date.now() : ''}`;
      const res = await fetch(url, {credentials:'same-origin',cache:force?'no-store':'default'});
      if (!res.ok) throw Error(`Producto ${handle}: HTTP ${res.status}`);
      const p = await res.json();
      if (!Array.isArray(p.variants)) throw Error('Producto sin variantes accesibles');
      const product = {id:String(p.id), title:String(p.title || ''), handle:String(p.handle || handle),
        tags:Array.isArray(p.tags)?p.tags:(typeof p.tags==='string'?p.tags.split(','):[]),
        variants:p.variants, url:'/products/'+encodeURIComponent(handle), image:p.featured_image || ''};
      this.productCache.set(handle, {at:Date.now(),product});
      return product;
    }
    async prepare(card) {
      const button = card.querySelector('[data-mf-prepare]');
      if (!button || card.dataset.details === 'yes') return;
      button.disabled = true; button.textContent = 'Consultando producto…';
      try {
        const p = await this.getProduct(card.dataset.handle, true);
        card._mfFullProduct = p;
        const form = node('div','mf-bv2-controls');
        const variantLabel = node('label','mf-bv2-wide','Variante / edición');
        const selectVariant = node('select'); selectVariant.dataset.mfVariant = '';
        for (const variant of p.variants) {
          const option = node('option',null,`${variant.title === 'Default Title' ? 'Presentación única' : variant.title} — ${this.money(finiteCents(variant.price))}`);
          option.value = String(variant.id); selectVariant.append(option);
        }
        variantLabel.append(selectVariant); form.append(variantLabel);
        const condLabel = node('label',null,'Condición');
        const condSel = node('select'); condSel.dataset.mfCondition = '';
        for (const [val,title] of [['NM','Near Mint'],['LP','Lightly Played'],['MP','Moderately Played'],['HP','Heavily Played'],['DMG','Dañada (DMG)']]) {
          const op = node('option',null,title); op.value = val; condSel.append(op);
        }
        condLabel.append(condSel); form.append(condLabel);
        const specialLabel = node('label','mf-bv2-wide','Autenticidad / alteraciones declaradas');
        const specialSelect = node('select'); specialSelect.dataset.mfSpecial = '';
        for (const [val,title] of [['normal','Original, sin alteraciones declaradas'],['altered','Firmada / alterada / reparada'],['fake','Reproducción / no auténtica']]) {
          const op = node('option',null,title); op.value = val; specialSelect.append(op);
        }
        specialLabel.append(specialSelect); form.append(specialLabel);
        const qtyLabel = node('label',null,'Copias');
        const qty = node('input'); qty.type='number'; qty.min='1'; qty.max='99999'; qty.step='1'; qty.value='1'; qty.dataset.mfQty = '';
        qtyLabel.append(qty); form.append(qtyLabel);
        const offer = node('p','mf-bv2-offer'); offer.dataset.mfOffer = '';
        const add = node('button','mf-bv2-add','Añadir a mi lista'); add.type='button'; add.dataset.mfAdd = '';
        button.replaceWith(form); card.querySelector('.mf-bv2-result-info').append(offer,add);
        card.dataset.details = 'yes'; this.updatePreview(card);
      } catch(error) { button.disabled=false; button.textContent='Reintentar cotización';
        card.querySelector('[data-mf-card-state]').textContent='No se pudo verificar el precio y las variantes en Shopify. Intenta nuevamente.';
        this.setStatus(`No se pudo consultar el producto en Shopify (${error.message}).`);
      }
    }
    chosen(card) {
      const p = card._mfFullProduct;
      const variantId = card.querySelector('[data-mf-variant]').value;
      const variant = p.variants.find(v=>String(v.id)===variantId);
      if (!variant) throw Error('Variante no encontrada');
      const condition=card.querySelector('[data-mf-condition]').value;
      const special=card.querySelector('[data-mf-special]').value;
      const qty=clampQty(card.querySelector('[data-mf-qty]').value);
      return {p,variant,condition,special,qty,assessed:this.assess(p,variant,condition,special)};
    }
    updatePreview(card) {
      try {
        const {assessed,qty}=this.chosen(card);
        const el=card.querySelector('[data-mf-card-state]');
        const price=card.querySelector('[data-mf-offer]');
        el.dataset.state=assessed.status;
        if (assessed.status === 'eligible') {
          el.replaceChildren();
          if (assessed.reason.startsWith('Estimación provisional. AVISO:')) {
            el.append(node('small', 'mf-bv2-quote-note', 'Sujeta a revisión de edición y acabado.'));
          }
          el.hidden = !el.childNodes.length;
        } else if (assessed.status === 'rejected') {
          // Al abrir el formulario tampoco mostrar la nota roja ni detalles de tags.
          el.replaceChildren();
          el.hidden = true;
        } else {
          el.hidden = false;
          el.textContent = assessed.reason;
        }
        const retail=card.querySelector('[data-mf-retail]');
        if (retail) retail.textContent = assessed.priceCents === null
          ? 'Precio de venta: no verificable'
          : `Precio de venta de esta variante: ${this.money(assessed.priceCents)}`;
        price.textContent=assessed.status==='eligible'
          ? `Oferta por copia: ${this.money(assessed.cashCents)} efectivo · ${this.money(assessed.creditCents)} crédito${qty > 1 ? ` · Total (${qty}): ${this.money(assessed.cashCents * qty)} efectivo` : ''}`
          : assessed.status==='rejected' ? 'No generamos oferta de compra para esta carta.'
          : 'Esta carta requiere revisión manual; no se muestra una oferta automática.';
      } catch(error) { this.setStatus(`No se pudo mostrar la cotización: ${error.message}`); }
    }
    addFromCard(card) {
      try {
        const {p,variant,condition,special,qty,assessed}=this.chosen(card);
        const candidate = {
          productId:String(p.id), variantId:String(variant.id), handle:p.handle, title:p.title,
          variantTitle:variant.title === 'Default Title' ? '' : String(variant.title),
          url:p.url, condition, special, qty, ...assessed
        };
        const previous=this.items.find(item=>this.keyFor(item)===this.keyFor(candidate));
        if (previous) { previous.qty=clampQty(previous.qty+qty); Object.assign(previous,{...assessed,title:candidate.title,variantTitle:candidate.variantTitle}); }
        else this.items.push(candidate);
        this.save(); this.renderCart();
        const button=card.querySelector('[data-mf-add]');
        button.textContent='¡Añadida!'; setTimeout(()=>{if(button.isConnected)button.textContent='Añadir a mi lista';},900);
      } catch(_) { this.setStatus('No se pudo agregar la carta. Revisa la variante elegida.'); }
    }
    makeGroup(title,items,flag){
      if(!items.length)return null;
      const group=node('section','mf-bv2-cart-group');
      group.append(node('h4',null,`${title} (${items.length})`));
      for(const item of items){
        const el=node('article','mf-bv2-cart-item');
        const row=node('div','mf-bv2-item-row');
        const left=node('div'); left.style.minWidth='0';
        const link=node('a','mf-bv2-item-title',item.title); link.href=item.url||'/'; link.target='_blank'; link.rel='noopener noreferrer';
        left.append(link);
        left.append(node('p','mf-bv2-meta',`${item.variantTitle ? item.variantTitle+' · ':''}${item.condition} · ${item.special==='altered'?'Alterada':item.special==='fake'?'No auténtica declarada':'Original declarada'}`));
        // La sección «No compramos» identifica las cartas rechazadas, sin
        // repetir notas técnicas ni tags dentro de cada producto.
        if (item.status === 'review') {
          left.append(node('p', 'mf-bv2-state', 'Pendiente de revisión por MasterForce.'));
        } else if (item.status === 'eligible' && item.reason.startsWith('Estimación provisional. AVISO:')) {
          left.append(node('p', 'mf-bv2-quote-note', 'Sujeta a revisión de edición y acabado.'));
        }
        row.append(left);
        if(item.status==='eligible'){
          const right=node('div','mf-bv2-item-price',this.money(item.cashCents*item.qty));
          right.append(node('small',null,`Crédito ${this.money(item.creditCents*item.qty)}`)); row.append(right);
        }
        el.append(row);
        const quantity=node('div','mf-bv2-qty-controls');
        const key=this.keyFor(item);
        for(const [action,label] of [['minus','−'],['value',String(item.qty)],['plus','+'],['remove','Quitar']]){
          if(action==='value'){quantity.append(node('span','mf-bv2-qty-value',label));continue;}
          const btn=node('button',null,label);btn.type='button';btn.dataset.action=action;btn.dataset.key=key;
          btn.setAttribute('aria-label',`${action==='minus'?'Reducir':action==='plus'?'Aumentar':'Quitar'} cantidad de ${item.title}`);
          quantity.append(btn);
        }
        el.append(quantity);
        const editLabel = node('label','mf-bv2-condition-edit','Cambiar condición ');
        const editSelect = node('select'); editSelect.dataset.mfChangeCondition = ''; editSelect.dataset.key = key;
        for (const val of ['NM','LP','MP','HP','DMG']) {
          const opt = node('option',null,val); opt.value=val; editSelect.append(opt);
        }
        editSelect.value=item.condition; editLabel.append(editSelect); el.append(editLabel);
        if(item.volume) el.append(node('p','mf-bv2-warning',`Revisión por volumen: ${this.config.volumeNotice} o más copias de esta variante.`));
        group.append(el);
      }
      return group;
    }
    renderCart() {
      this.cartBox.replaceChildren();
      const perVariant=new Map();
      this.items.forEach(i=>perVariant.set(i.variantId,(perVariant.get(i.variantId)||0)+i.qty));
      this.items.forEach(i=>i.volume=(perVariant.get(i.variantId)||0)>=this.config.volumeNotice);
      const eligible=this.items.filter(i=>i.status==='eligible');
      const rejected=this.items.filter(i=>i.status==='rejected');
      const review=this.items.filter(i=>i.status!=='eligible'&&i.status!=='rejected');
      if(!this.items.length) this.cartBox.append(node('p','mf-bv2-empty','Tu lista está vacía. Busca y agrega cartas.'));
      else for(const [title,group] of [['Cotizables',eligible],['No compramos',rejected],['Requieren revisión',review]]){
        const result=this.makeGroup(title,group);if(result)this.cartBox.append(result);
      }
      const qtyTotal=this.items.reduce((a,i)=>a+i.qty,0);
      const cash=eligible.reduce((a,i)=>a+i.cashCents*i.qty,0);
      const credit=eligible.reduce((a,i)=>a+i.creditCents*i.qty,0);
      this.root.querySelector('[data-mf-count]').textContent=`${qtyTotal} carta${qtyTotal===1?'':'s'}`;
      this.root.querySelector('[data-mf-total-cash]').textContent=this.money(cash);
      this.root.querySelector('[data-mf-total-credit]').textContent=this.money(credit);
      this.sendBtn.disabled=this.items.length===0;
      this.clearBtn.disabled=this.items.length===0;
    }
    save() {
      try {
        if(!this.items.length){localStorage.removeItem(this.key);return;}
        const minimal=this.items.map(({productId,variantId,handle,title,variantTitle,url,condition,special,qty})=>({productId,variantId,handle,title,variantTitle,url,condition,special,qty}));
        localStorage.setItem(this.key,JSON.stringify({version:2,expiresAt:Date.now()+this.config.storageDays*86400000,items:minimal}));
      } catch (_) { /* Storage puede estar bloqueado: el cotizador sigue funcionando. */ }
    }
    restore() {
      try {
        const raw=localStorage.getItem(this.key);if(!raw)return;
        const saved=JSON.parse(raw);
        if(saved.version!==2||Date.now()>saved.expiresAt||!Array.isArray(saved.items)){localStorage.removeItem(this.key);return;}
        this.items=saved.items.filter(i=>i.handle&&i.variantId&&['NM','LP','MP','HP','DMG'].includes(i.condition))
          .slice(0,250).map(i=>({
            productId:String(i.productId||''),variantId:String(i.variantId),handle:String(i.handle),
            title:String(i.title||''),variantTitle:String(i.variantTitle||''),url:'/products/'+encodeURIComponent(i.handle),
            condition:i.condition,special:['normal','altered','fake'].includes(i.special)?i.special:'normal',qty:clampQty(i.qty),
            status:'review',reason:'Verificando clasificación y precio actual…',cashCents:0,creditCents:0,priceCents:null
          }));
        this.renderCart();
        this.revalidate(false).then(()=>this.renderCart());
      } catch (_) { /* no interrumpir si el almacenamiento fue alterado */ }
    }
    async revalidate(force=true) {
      const byHandle=new Map();
      for(const item of this.items){
        if(!byHandle.has(item.handle))byHandle.set(item.handle,[]);
        byHandle.get(item.handle).push(item);
      }
      const handles=[...byHandle.keys()];
      for(let cursor=0;cursor<handles.length;cursor+=4){
        await Promise.all(handles.slice(cursor,cursor+4).map(async handle=>{
          const lines=byHandle.get(handle);
          try {
            const p=await this.getProduct(handle,force);
            for(const item of lines){
              const variant=p.variants.find(v=>String(v.id)===item.variantId);
              if(!variant){Object.assign(item,{status:'review',reason:'La variante ya no está disponible para consulta.',cashCents:0,creditCents:0});continue;}
              Object.assign(item,this.assess(p,variant,item.condition,item.special),{
                productId:String(p.id),title:p.title,
                variantTitle:variant.title==='Default Title'?'':String(variant.title),url:p.url
              });
            }
          }catch(_){
            for(const item of lines)Object.assign(item,{status:'review',reason:'No se pudo verificar el producto en Shopify.',cashCents:0,creditCents:0});
          }
        }));
      }
      this.renderCart();
    }
    snapshot(){return new Map(this.items.map(i=>[this.keyFor(i),{status:i.status,cashCents:i.cashCents,creditCents:i.creditCents,reason:i.reason}]));}
    buildMessage(){
      // Mensaje para el cliente: sin IDs de Shopify, diagnósticos de tags ni folios internos.
      // Los identificadores y el motivo técnico se conservan en los objetos de la lista
      // para la validación del producto, pero no se incluyen en el texto de WhatsApp.
      const now = new Date();
      const ref = `MF-BUY-${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}-${Math.random().toString(36).slice(2,9).toUpperCase()}`;
      const eligible = this.items.filter(i => i.status === 'eligible');
      const rejected = this.items.filter(i => i.status === 'rejected');
      const review = this.items.filter(i => i.status !== 'eligible' && i.status !== 'rejected');
      const perVariant = new Map();
      this.items.forEach(i => perVariant.set(i.variantId, (perVariant.get(i.variantId) || 0) + i.qty));
      const quantity = this.items.reduce((sum, item) => sum + item.qty, 0);
      const totalCash = eligible.reduce((sum, i) => sum + i.cashCents * i.qty, 0);
      const totalCredit = eligible.reduce((sum, i) => sum + i.creditCents * i.qty, 0);
      const lines = [
        '🐉 *MASTERFORCE TCG — SOLICITUD DE VENTA*',
        '',
        'Hola, me gustaría vender las siguientes cartas:',
        ''
      ];
      const titleFor = item => `${item.title}${item.variantTitle ? ' — ' + item.variantTitle : ''}`;
      const productLink = item => {
        try { return new URL(item.url, location.origin).href; }
        catch (_) { return ''; }
      };
      const volumeLine = item => (perVariant.get(item.variantId) || 0) >= this.config.volumeNotice
        ? 'Nota: cantidad sujeta a revisión por volumen.' : '';
      const publicReason = item => {
        const reason = String(item.reason || '').toLowerCase();
        if (item.condition === 'DMG' || reason.includes('dañad')) return 'No adquirimos cartas dañadas.';
        if (item.special === 'fake' || reason.includes('falsificad') || reason.includes('reproduccion')) return 'No adquirimos reproducciones ni cartas no auténticas.';
        if (reason.includes('common') || reason.includes('uncommon')) return 'Esta categoría de carta no participa en nuestro programa de compra.';
        if (reason.includes('reverse holo')) return 'Actualmente no adquirimos esta versión de la carta.';
        if (reason.includes('inferior al mínimo')) return 'No alcanza el mínimo de compra por carta.';
        if (item.special === 'altered' || reason.includes('alterad') || reason.includes('firmad')) return 'Se requiere valoración manual por su condición especial.';
        if (reason.includes('precio') || reason.includes('moneda')) return 'Precio pendiente de verificación.';
        if (reason.includes('clasific') || reason.includes('rareza') || reason.includes('tags') || reason.includes('tag')) return 'Clasificación pendiente de revisión.';
        if (reason.includes('variante') || reason.includes('producto')) return 'Producto o variante pendiente de verificación.';
        return item.status === 'rejected' ? 'No es elegible para compra.' : 'Sujeta a revisión de MasterForce.';
      };
      if (eligible.length) {
        lines.push('*CARTAS COTIZABLES*', '');
        eligible.forEach((item, idx) => {
          lines.push(`*${idx + 1}. ${titleFor(item)}*`,
            `Condición: ${item.condition} | Cantidad: ${item.qty}`,
            `Efectivo por carta: ${this.money(item.cashCents)} | Subtotal: *${this.money(item.cashCents * item.qty)}*`,
            `Crédito por carta: ${this.money(item.creditCents)} | Subtotal: *${this.money(item.creditCents * item.qty)}*`);
          // Una contradicción de clasificación no debe desaparecer de la revisión comercial,
          // pero el diagnóstico de tags corresponde al personal, no al mensaje al cliente.
          if (String(item.reason || '').startsWith('Estimación provisional. AVISO:')) {
            lines.push('Nota: edición y acabado sujetos a verificación.');
          }
          const volume = volumeLine(item);
          if (volume) lines.push(volume);
          const url = productLink(item);
          if (url) lines.push(`Ver carta: ${url}`);
          lines.push('');
        });
      }
      if (rejected.length) {
        lines.push('*CARTAS QUE NO COMPRAMOS*', '');
        rejected.forEach((item, idx) => {
          lines.push(`${idx + 1}. ${titleFor(item)} — ${item.condition} · ${item.qty} copia(s)`, publicReason(item));
          const url = productLink(item);
          if (url) lines.push(`Ver carta: ${url}`);
          lines.push('');
        });
      }
      if (review.length) {
        lines.push('*CARTAS PARA REVISIÓN*', '');
        review.forEach((item, idx) => {
          lines.push(`${idx + 1}. ${titleFor(item)} — ${item.condition} · ${item.qty} copia(s)`, publicReason(item));
          const volume = volumeLine(item);
          if (volume) lines.push(volume);
          const url = productLink(item);
          if (url) lines.push(`Ver carta: ${url}`);
          lines.push('');
        });
      }
      lines.push('*RESUMEN DE COTIZACIÓN*',
        `Total de cartas: ${quantity}`,
        `💵 *Efectivo estimado: ${this.money(totalCash)}*`,
        `💳 *Crédito estimado: ${this.money(totalCredit)}*`,
        '',
        'Los importes son estimaciones sujetas a revisión física, autenticidad, edición, condición y aprobación de MasterForce TCG.',
        '',
        '¿Podrían confirmar si les interesa adquirirlas?');
      return {message:lines.join('\n'), ref, eligible, rejected, review};
    }
    async reviewForWhatsApp(){
      if(!this.items.length)return;
      this.sendBtn.disabled=true;this.sendBtn.textContent='Verificando precios y clasificación…';
      const before=this.snapshot();
      try {
        await this.revalidate(true);
        this.save();
        const result=this.buildMessage();
        this.copyText=result.message;
        const changes=[];
        for(const item of this.items){
          const old=before.get(this.keyFor(item));
          if(!old)continue;
          if(old.status!==item.status)changes.push(`${item.title}: estado ${old.status} → ${item.status}.`);
          else if(old.status==='eligible' && (old.cashCents!==item.cashCents||old.creditCents!==item.creditCents))
            changes.push(`${item.title} (${item.condition}): efectivo por copia ${this.money(old.cashCents)} → ${this.money(item.cashCents)}; crédito ${this.money(old.creditCents)} → ${this.money(item.creditCents)}.`);
        }
        const summary=`${result.eligible.length} líneas cotizables, ${result.rejected.length} rechazadas y ${result.review.length} en revisión. Comprueba los importes antes de enviar.`;
        this.root.querySelector('[data-mf-review-summary]').textContent=summary;
        const container=this.root.querySelector('[data-mf-changes]');container.replaceChildren();
        if(!changes.length)container.append(node('p',null,'Sin cambios detectados desde la última valoración.'));
        else {container.append(node('h4',null,'Cambios detectados:'));changes.slice(0,100).forEach(text=>container.append(node('p',null,text)));}
        const digits=String(this.config.waNumber||'').replace(/\D/g,'');
        const wa=this.root.querySelector('[data-mf-wa-link]');
        const long=encodeURIComponent(result.message).length>6800;
        this.root.querySelector('[data-mf-long-notice]').hidden=!long;
        if(!digits || digits.length<10){
          wa.removeAttribute('href');wa.setAttribute('aria-disabled','true');wa.textContent='Configura el número de WhatsApp';
        }else{
          const body=long?'Hola, tengo una lista extensa de cartas para cotizar. Copiaré y pegaré el detalle completo en este chat.':result.message;
          wa.href=`https://wa.me/${digits}?text=${encodeURIComponent(body)}`;
          wa.removeAttribute('aria-disabled');wa.textContent=long?'Abrir WhatsApp y pegar la lista':'Confirmar y abrir WhatsApp';
        }
        this.dialog.showModal();
      } catch(_) {this.setStatus('No se pudo terminar la revisión de la lista. Intenta nuevamente.');}
      finally{this.sendBtn.disabled=false;this.sendBtn.textContent='Revisar y preparar WhatsApp';}
    }
    async copyToClipboard(){
      const btn=this.dialog.querySelector('[data-mf-copy]');
      try {
        await navigator.clipboard.writeText(this.copyText);
        btn.textContent='Lista copiada';setTimeout(()=>{if(btn.isConnected)btn.textContent='Copiar lista completa';},1500);
      } catch(_) {btn.textContent='No se pudo copiar; selecciona el texto manualmente.';}
    }
    destroy(){this.events.abort();this.cancelSearch();if(this.dialog.open)this.dialog.close();instances.delete(this.root);}
  }
  const init=(parent=document)=>{
    const roots=[];
    if(parent.matches&&parent.matches('[data-mf-buylist-v2]'))roots.push(parent);
    roots.push(...parent.querySelectorAll('[data-mf-buylist-v2]'));
    for(const root of roots){if(!instances.has(root)){try{instances.set(root,new Buylist(root));}catch(e){console.error('[MF Buylist V2]',e);}}}
  };
  window[GLOBAL]={init};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>init(),{once:true});
  else init();
  document.addEventListener('shopify:section:load',event=>init(event.target));
  document.addEventListener('shopify:section:unload',event=>{
    const root=event.target.matches('[data-mf-buylist-v2]')?event.target:event.target.querySelector('[data-mf-buylist-v2]');
    if(root&&instances.has(root))instances.get(root).destroy();
  });
})();
